import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, CalendarClock, Package, RotateCcw, ShoppingBag, TimerReset } from "lucide-react";
import { MEMBERSHIP_STATUS_LABELS, type MembershipClock, type MembershipStatus } from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Badge, Button, type BadgeVariant } from "@/shared/ui/core";
import { SkeletonCard } from "@/shared/ui/ui";
import { KpiCard } from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";
import { useMembershipCountdown, useMyMembership } from "./membership.api";
import { AnnualMembershipCard } from "./dashboard/AnnualMembershipCard";

export const MEMBERSHIP_STATUS_VARIANTS: Record<MembershipStatus, BadgeVariant> = {
  none: "neutral",
  active: "success",
  expiring: "warning",
  expired: "error",
};

/** La frase de cada estado, como en producción (`RelojRecompra.tsx`). */
export const MEMBERSHIP_STATUS_HINTS: Record<MembershipStatus, string> = {
  none: "Aún no tienes una primera compra validada. Tu primer pedido arranca el ciclo.",
  active: "Tu membresía está activa.",
  expiring: "Tu membresía está por vencer. Haz tu recompra para no perder tu posición.",
  expired: "Tu membresía venció. Recompra para reiniciar el ciclo.",
};

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * El RELOJ DE RECOMPRA — porte de la pantalla de producción.
 *
 * El socio tiene un ciclo (30 días por defecto) que arranca en cada
 * orden validada. Aquí lo ve EN GRANDE: cuánto falta al segundo, cuándo
 * arrancó, cuándo vence, su estado, y el paquete que lo arrancó. Y una
 * sola acción: recomprar (o hacer la primera compra).
 *
 * Nada se calcula aquí que el servidor no haya dicho: la fecha, el
 * estado y el ciclo vienen del API; la pantalla solo descompone lo que
 * falta para pintarlo cada segundo (`useMembershipCountdown`).
 * ═══════════════════════════════════════════════════════════════════
 */
export function RelojRecompraPage() {
  const { t } = useTranslation();
  const clock = useMyMembership();

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Reloj de recompra")}
        description={t("Tu ciclo de membresía: cada pedido validado lo reinicia.")}
      />
      {clock.isPending ? <SkeletonCard /> : clock.data ? <ClockScreen clock={clock.data} /> : null}
    </>
  );
}

function ClockScreen({ clock }: { clock: MembershipClock }) {
  const { t } = useTranslation();
  const left = useMembershipCountdown(clock);
  const overdue = clock.status === "expired" || clock.status === "none";
  const ctaLabel = clock.status === "none" ? t("Hacer mi primera compra") : t("Hacer recompra");

  return (
    <div className="grid gap-4">
      {/* ── La cuenta regresiva, en grande ─────────────────────────────── */}
      <section
        aria-label={t("Cuenta regresiva")}
        className="relative overflow-hidden rounded-3xl bg-ds-ink p-6 text-white shadow-ds-float sm:p-8"
      >
        <div className="relative grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
          <div className="grid gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white/10">
                <TimerReset className="h-4.5 w-4.5" />
              </span>
              <p className="text-[12px] font-semibold uppercase tracking-ds-label text-white/70">
                {t("Recompra · próximo ciclo")}
              </p>
              <Badge variant={MEMBERSHIP_STATUS_VARIANTS[clock.status]} size="sm" dot>
                {t(MEMBERSHIP_STATUS_LABELS[clock.status])}
              </Badge>
            </div>

            <p
              className="font-mono text-[44px] font-semibold leading-none tracking-tight sm:text-[56px]"
              data-testid="countdown"
            >
              {left.days}
              <span className="ml-2 text-[18px] font-medium text-white/70">{t("días")}</span>
              <span className="ml-4 text-[26px] font-medium text-white/85 sm:text-[30px]">
                {pad(left.hours)}:{pad(left.minutes)}:{pad(left.seconds)}
              </span>
            </p>

            <p className="max-w-xl text-[13.5px] leading-relaxed text-white/75">
              {overdue
                ? t(MEMBERSHIP_STATUS_HINTS[clock.status])
                : t("Te quedan {{days}} días, {{hours}}h {{minutes}}m {{seconds}}s para tu próxima recompra.", {
                    days: left.days,
                    hours: left.hours,
                    minutes: left.minutes,
                    seconds: left.seconds,
                  })}
            </p>
          </div>

          <Button
            as={Link}
            to={clock.status === "none" ? "/paquetes" : "/shop"}
            size="lg"
            leftIcon={overdue ? <ShoppingBag /> : <RotateCcw />}
            rightIcon={<ArrowRight />}
            className="bg-white text-ds-ink hover:bg-white/90"
          >
            {ctaLabel}
          </Button>
        </div>
      </section>

      {/* ── Las fechas del ciclo ───────────────────────────────────────── */}
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
        <KpiCard
          icon={CalendarClock}
          item="inicio-ciclo"
          label={t("Inicio del ciclo")}
          value={clock.startedAt ? formatOrderDate(clock.startedAt) : "—"}
          hint={clock.lastOrderFolio ? t("Pedido {{folio}}", { folio: clock.lastOrderFolio }) : undefined}
        />
        <KpiCard
          icon={CalendarClock}
          item="fecha-recompra"
          label={t("Próxima fecha de recompra")}
          value={clock.validUntil ? formatOrderDate(clock.validUntil) : "—"}
          hint={t("Ciclo de {{n}} días", { n: clock.cycleDays })}
        />
        <KpiCard
          icon={TimerReset}
          item="estado-membresia"
          label={t("Estado de la membresía")}
          value={t(MEMBERSHIP_STATUS_LABELS[clock.status])}
          hint={
            clock.expiringSoonDays > 0
              ? t("Aviso {{n}} días antes de vencer", { n: clock.expiringSoonDays })
              : undefined
          }
        />
      </div>

      {/* ── La membresía ANUAL (14-sep-2026) ───────────────────────────── */}
      <AnnualMembershipCard annual={clock.annual} />

      {/* ── Tu paquete actual ──────────────────────────────────────────── */}
      <section className="relative rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
        <div className="relative grid gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[15px] font-semibold text-ds-ink">{t("Tu paquete actual")}</h2>
            <Button as={Link} to="/paquetes" variant="ghost" size="sm" rightIcon={<ArrowRight />}>
              {t("Ver paquetes")}
            </Button>
          </div>
          {clock.activePackageName ? (
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-ds-border bg-white px-3.5 py-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-ds-violet-pop/10 text-ds-violet-pop">
                <Package className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-medium text-ds-ink">{clock.activePackageName}</p>
                <p className="text-[12px] text-ds-ink-3">
                  {clock.startedAt
                    ? t("Validado el {{date}}", { date: formatOrderDate(clock.startedAt) })
                    : null}
                </p>
              </div>
              <Badge variant={MEMBERSHIP_STATUS_VARIANTS[clock.status]} size="sm">
                {t(MEMBERSHIP_STATUS_LABELS[clock.status])}
              </Badge>
            </div>
          ) : (
            <p className="text-[13px] text-ds-ink-3">
              {t("Aún no tienes paquetes. Tu primer pedido validado arranca tu ciclo de recompra.")}
            </p>
          )}
        </div>
      </section>

      {/* ── El recordatorio, como en producción ────────────────────────── */}
      <section className="rounded-3xl border border-ds-border bg-ds-cream-2/60 p-5">
        <h2 className="text-[14px] font-semibold text-ds-ink">{t("Mantén tu nivel activo")}</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-ds-ink-3">
          {t("Cada pedido validado reinicia tu ciclo de {{n}} días. Mientras tu membresía esté activa, tus invitados y tus puntos cuentan para tu rango; si vence, dejas de acumular hasta que recompres.", {
            n: clock.cycleDays,
          })}
        </p>
      </section>
    </div>
  );
}
